import { Minus, Plus } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export type CartActionGroupProps = {
  lineId: string;
  lineQuantity: number;
  variantName: string;
  editable: boolean;
  adjustOrderLine?: (lineId: string, quantity: number) => void;
  removeItem?: (lineId: string) => void;
};

export function CartActionGroup({
  lineId,
  editable,
  variantName,
  lineQuantity,
  adjustOrderLine,
  removeItem,
}: CartActionGroupProps) {
  const { t } = useTranslation();

  const [localQuantity, setLocalQuantity] = useState<string | number>(
    lineQuantity,
  );

  useEffect(() => {
    setLocalQuantity(lineQuantity);
  }, [lineQuantity]);

  const handleQuantityUpdate = useCallback(() => {
    try {
      if (localQuantity === '') {
        setLocalQuantity(lineQuantity);
        return;
      }

      const parsed = Number(localQuantity);
      if (!isNaN(parsed) && parsed >= 1) {
        if (parsed !== lineQuantity) {
          adjustOrderLine?.(lineId, parsed);
        }
        setLocalQuantity(parsed);
      } else {
        setLocalQuantity(lineQuantity);
      }
    } catch (error) {
      console.error(
        `Error adjusting quantity for lineId=${lineId}, variant=${variantName}:`,
        error,
      );
      setLocalQuantity(lineQuantity);
    }
  }, [localQuantity, lineQuantity, lineId, variantName, adjustOrderLine]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setLocalQuantity(e.target.value);
    },
    [],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        handleQuantityUpdate();
      }
    },
    [handleQuantityUpdate],
  );

  const handleIncrement = useCallback(() => {
    try {
      const newQuantity =
        (typeof localQuantity === 'number'
          ? localQuantity
          : Number(localQuantity) || lineQuantity) + 1;
      adjustOrderLine?.(lineId, newQuantity);
      setLocalQuantity(newQuantity);
    } catch (error) {
      console.error(`Error incrementing quantity for lineId=${lineId}:`, error);
    }
  }, [localQuantity, adjustOrderLine, lineId, lineQuantity]);

  const handleDecrement = useCallback(() => {
    try {
      const current =
        typeof localQuantity === 'number'
          ? localQuantity
          : Number(localQuantity) || lineQuantity;

      if (current === 1 && removeItem) {
        removeItem(lineId);
      } else {
        const newQuantity = Math.max(current - 1, 1);
        adjustOrderLine?.(lineId, newQuantity);
        setLocalQuantity(newQuantity);
      }
    } catch (error) {
      console.error(`Error decrementing quantity for lineId=${lineId}:`, error);
      alert(t('cart.decrementError'));
    }
  }, [localQuantity, adjustOrderLine, removeItem, lineId, lineQuantity, t]);

  return (
    <div className="flex items-center gap-0.5 sm:gap-1">
      {/* Decrement Button */}
      <button
        type="button"
        className="flex-shrink-0 w-8 h-8 md:w-7 md:h-7 rounded-md sm:rounded-lg border-2 border-gray-300 bg-white hover:bg-primary hover:border-primary hover:text-white text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary/30 flex items-center justify-center active:scale-95 font-bold"
        onClick={handleDecrement}
        disabled={!editable}
        aria-label={t('cart.decreaseQuantity')}
      >
        <Minus className="w-4 h-4 md:w-3.5 md:h-3.5" strokeWidth={2.5} />
      </button>

      {/* Quantity Input */}
      <div className="flex-shrink-0">
        <label htmlFor={`quantity-${lineId}`} className="sr-only">
          {t('common.quantity')}
        </label>
        <input
          type="text"
          id={`quantity-${lineId}`}
          name={`quantity-${lineId}`}
          value={localQuantity}
          onChange={handleInputChange}
          onBlur={handleQuantityUpdate}
          onKeyDown={handleKeyDown}
          disabled={!editable}
          inputMode="numeric"
          pattern="[0-9]*"
          className="box-border w-10 h-8 md:h-7 bg-gray-50 border-2 border-gray-300 rounded-md sm:rounded-lg text-sm font-bold text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-center transition-all duration-200 align-middle"
          style={{ padding: '0 2px', lineHeight: '18px' }}
          aria-label={`Set quantity for ${variantName}`}
        />
      </div>

      {/* Increment Button */}
      <button
        type="button"
        className="flex-shrink-0 w-8 h-8 md:w-7 md:h-7 rounded-md sm:rounded-lg border-2 border-primary bg-primary hover:bg-primary/90 text-white disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary/30 flex items-center justify-center active:scale-95 shadow-sm hover:shadow-md font-bold"
        onClick={handleIncrement}
        disabled={!editable}
        aria-label={t('cart.increaseQuantity')}
      >
        <Plus className="w-4 h-4 md:w-3.5 md:h-3.5" strokeWidth={2.5} />
      </button>
    </div>
  );
}
